import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Download, OpenInNew, Refresh, Send } from '@mui/icons-material';

import { CONFIG } from '@frontend/config';

import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import {
  apiErrorMessage,
  apiErrorStatus,
  fetchVoucherFile,
  useRequestVoucher,
  useSendVoucher,
  useVoucherState,
  voucherLangOf,
} from '../queries';
import { copyTicketUrl } from '../ticket-utils';
import {
  VoucherActionBooking,
  voucherActionView,
  voucherFilenameFrom,
  voucherPollingDecision,
  voucherRowChip,
  voucherSendAvailable,
  voucherAttemptAfter,
  voucherAttemptFor,
  voucherAttemptUnbound,
  voucherAttemptsReleased,
  voucherSendAttemptStale,
  voucherSendNotes,
  voucherSendPolling,
  voucherSendRequestOutcome,
  voucherSendToastKey,
  type VoucherAttemptInFlight,
  type VoucherAttemptsInFlight,
  voucherShareUrl,
  voucherSurfaceVisible,
} from '../voucherAction';
import { defaultAttemptIdFactory } from '../holdAttemptIdentity';

const KEY = 'travelServices.booking.voucher';

/**
 * El voucher de agencia de una reserva: su estado y lo que el asesor puede
 * hacer con él. Se monta en el detalle, en el post-hold y en la fila del
 * listado, y decide SOLO su visibilidad (`voucherSurfaceVisible`): ningún
 * contenedor puede mostrar lo que otro esconde.
 *
 * Es un componente de presentación sobre `voucherAction.ts`: las decisiones
 * (qué vista, si seguir preguntando, cómo se llama el archivo) viven ahí, con
 * sus tests.
 */
export function VoucherAction({
  booking,
  variant = 'section',
  allowSend = true,
}: {
  booking: VoucherActionBooking;
  variant?: 'section' | 'row';
  allowSend?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const { addToast } = useSnackBar();
  const lang = voucherLangOf(i18n.language);
  const visible = voucherSurfaceVisible(booking);

  /*
   * El pedido en vuelo: desde cuándo, y qué documento había antes. Con eso
   * el polling sabe cuándo apareció uno NUEVO y cuándo rendirse.
   */
  const [requestedAt, setRequestedAt] = useState<number | null>(null);
  const baseline = useRef<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [downloading, setDownloading] = useState<'view' | 'download' | null>(null);

  const stateQuery = useVoucherState(booking, {
    enabled: visible,
    lang,
    background: variant === 'row',
  });

  // Los dos modales cambian de reserva sin desmontarse: el pedido en vuelo y
  // la descarga son de la reserva que los produjo, no de la que llegue después.
  useEffect(() => {
    setRequestedAt(null);
    baseline.current = null;
    setDownloading(null);
  }, [booking.id]);
  const decision = voucherPollingDecision({
    requestedAt,
    now,
    state: stateQuery.data,
    baselineGeneratedAt: baseline.current,
  });

  useEffect(() => {
    if (decision.kind !== 'poll') return undefined;
    const timer = setTimeout(() => {
      setNow(Date.now());
      void stateQuery.refetch();
    }, decision.intervalMs);
    return () => clearTimeout(timer);
    // `stateQuery` cambia de identidad en cada render; lo que importa es la decisión.
  }, [decision.kind, decision.kind === 'poll' ? decision.intervalMs : 0, now]);

  useEffect(() => {
    if (decision.kind === 'done') {
      setRequestedAt(null);
      addToast({ message: t(`${KEY}.ready`), color: 'success' });
    }
    // Ya no se puede emitir: el motivo está en pantalla; nada que festejar.
    if (decision.kind === 'unavailable') setRequestedAt(null);
    if (decision.kind === 'timedOut') {
      setRequestedAt(null);
      addToast({ message: t(`${KEY}.stillGenerating`), color: 'warning' });
    }
  }, [decision.kind]);

  const request = useRequestVoucher();
  const generate = () => {
    baseline.current =
      stateQuery.data?.status === 'generated' ? (stateQuery.data.generatedAt ?? null) : null;
    request.mutate(
      { bookingId: booking.id, agencyId: booking.agencyId, lang },
      {
        onSuccess: () => {
          setRequestedAt(Date.now());
          setNow(Date.now());
        },
        onError: (error) =>
          addToast({
            message: apiErrorMessage(error) ?? t(`${KEY}.requestFailed`),
            color: 'error',
          }),
      },
    );
  };

  const openFile = async (mode: 'view' | 'download') => {
    setDownloading(mode);
    /*
     * La pestaña de "Ver" se abre ANTES de bajar el archivo, dentro del clic:
     * después de un `await` el bloqueador de popups la corta y, con `noopener`,
     * `window.open` devuelve `null` siempre, así que ni se podía saber. Si igual
     * no se pudo abrir, se cae a la descarga: "Ver" nunca termina en silencio.
     */
    const tab = mode === 'view' ? window.open('', '_blank') : null;
    if (tab) tab.opener = null;
    try {
      const { blob, contentDisposition } = await fetchVoucherFile({
        bookingId: booking.id,
        agencyId: booking.agencyId,
      });
      const url = URL.createObjectURL(blob);
      if (tab) {
        tab.location.href = url;
      } else {
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = voucherFilenameFrom(contentDisposition, `voucher-${booking.id}.pdf`);
        anchor.click();
      }
      // El objeto vive lo que tarda el navegador en tomarlo.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (error) {
      tab?.close();
      // Con `responseType: 'blob'` el cuerpo del error no es JSON: se decide
      // por el status. Solo el 404 significa "ya no está, regeneralo"; un
      // 403 o un 5xx no pueden mandar a regenerar un documento que existe.
      const gone = apiErrorStatus(error) === 404;
      addToast({
        message: t(gone ? `${KEY}.fileUnavailable` : `${KEY}.fileFailed`),
        color: 'error',
      });
      if (gone) void stateQuery.refetch();
    } finally {
      setDownloading(null);
    }
  };

  /*
   * ENVIAR. Un menú con las salidas que existen hoy: mail al contacto del hold
   * y link para compartir (WhatsApp llega aparte: exige un cliente con
   * conversación, que la reserva no tiene). El resultado del backend se
   * muestra tal cual —`no_email` y `failed` son estados, no errores— y el link
   * se copia; si el portapapeles falla, se ofrece a mano en un campo.
   */
  const [sendAnchor, setSendAnchor] = useState<HTMLElement | null>(null);
  const [shareLink, setShareLink] = useState<{ url: string; copied: boolean } | null>(null);
  // Los intentos ATADOS por canal (sin respuesta, o `pending`): el próximo
  // clic por ese canal los reintenta con su id en vez de abrir otro.
  const [bound, setBound] = useState<VoucherAttemptsInFlight>({});
  const send = useSendVoucher();
  useEffect(() => {
    setSendAnchor(null);
    setShareLink(null);
    setBound({});
  }, [booking.id]);
  // El estado publica el desenlace de un intento atado: se suelta solo.
  const lastSends = stateQuery.data?.lastSends;
  useEffect(() => {
    const released = voucherAttemptsReleased(bound, lastSends);
    if (released !== bound) setBound(released);
  }, [bound, lastSends]);
  // Mientras haya un envío `pending`, el estado se vuelve a pedir solo.
  const sendPolling = voucherSendPolling(stateQuery.data);
  const [sendTick, setSendTick] = useState(0);
  useEffect(() => {
    if (sendPolling.kind !== 'poll') return undefined;
    const timer = setTimeout(() => {
      setSendTick((n) => n + 1);
      void stateQuery.refetch();
    }, sendPolling.intervalMs);
    return () => clearTimeout(timer);
  }, [sendPolling.kind, sendTick]);
  const sendBy = (channel: 'email' | 'link', options: { fresh?: boolean } = {}) => {
    if (!allowSend || booking.source === 'hotelrs') return;
    setSendAnchor(null);
    const attempt: VoucherAttemptInFlight = {
      bookingId: booking.id,
      channel,
      // `fresh`: el intento atado era de otro documento; se abre uno nuevo.
      attemptId: options.fresh
        ? defaultAttemptIdFactory()
        : voucherAttemptFor(booking.id, channel, bound, lastSends, defaultAttemptIdFactory),
    };
    send.mutate(
      { bookingId: booking.id, agencyId: booking.agencyId, channel, attemptId: attempt.attemptId },
      {
        onSuccess: async (result) => {
          setBound((previous) => voucherAttemptAfter(result, attempt, previous));
          if (result.channel === 'link') {
            const url = voucherShareUrl(CONFIG.API_URL, result.path);
            const copied = (await copyTicketUrl(url))?.outcome === 'copied';
            setShareLink({ url, copied });
            // Copiado sí, pero un link `partial` no lleva el ticket del operador.
            const partial = result.outcome === 'partial';
            addToast({
              message: t(
                !copied
                  ? `${KEY}.send.copyFailed`
                  : partial
                    ? `${KEY}.send.copiedPartial`
                    : `${KEY}.send.copied`,
              ),
              color: copied && !partial ? 'success' : 'warning',
            });
            return;
          }
          const toast = voucherSendToastKey(result.outcome);
          addToast({
            message: t(toast.key, { recipient: result.recipient ?? '' }),
            color: toast.color,
          });
        },
        onError: (error) => {
          if (channel === 'link' && !options.fresh && voucherSendAttemptStale(error)) {
            // El link atado era de un documento anterior: uno nuevo, una vez.
            setBound((previous) => voucherAttemptUnbound(previous, 'link'));
            sendBy('link', { fresh: true });
            return;
          }
          if (voucherSendRequestOutcome(error) === 'unanswered') {
            // Sin respuesta no se dice "no salió": el intento queda, y el
            // estado (que se refresca) muestra cómo terminó.
            setBound((previous) => ({ ...previous, [channel]: attempt }));
            addToast({ message: t(`${KEY}.send.requestUnanswered`), color: 'warning' });
            return;
          }
          addToast({
            message: apiErrorMessage(error) ?? t(`${KEY}.send.requestFailed`),
            color: 'error',
          });
        },
      },
    );
  };

  if (!visible) return null;

  if (variant === 'row') {
    const chip = voucherRowChip(stateQuery.data);
    if (!chip) return null;
    const color = chip === 'ready' ? 'success' : chip === 'stale' ? 'warning' : 'default';
    return <Chip size="small" color={color} variant="outlined" label={t(`${KEY}.row.${chip}`)} />;
  }

  const view = voucherActionView(stateQuery.data, {
    vertical: booking.vertical,
    isError: stateQuery.isError,
  });
  const generating = request.isLoading || requestedAt !== null;

  if (view.kind === 'loading') return null;
  if (view.kind === 'error') {
    return (
      <Alert severity="info">
        {apiErrorMessage(stateQuery.error) ?? t(`${KEY}.stateUnavailable`)}
      </Alert>
    );
  }
  if (view.kind === 'unsupported') {
    return (
      <Typography variant="caption" color="text.secondary">
        {t(`${KEY}.unsupported`, { vertical: t(`rutero.verticals.${booking.vertical}`) })}
        {view.alternativeKey ? ` ${t(view.alternativeKey)}` : ''}
      </Typography>
    );
  }
  if (view.kind === 'notReady') {
    return (
      <Box display="flex" flexDirection="column" gap={1}>
        <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
          <Typography variant="subtitle2">{t(`${KEY}.title`)}</Typography>
          {/* Un botón gris nunca es mudo: el motivo va al lado, y en el tooltip. */}
          <Tooltip title={t(view.reasonKey)}>
            <span>
              <Button size="small" variant="outlined" disabled>
                {t(`${KEY}.generate.voucher`)}
              </Button>
            </span>
          </Tooltip>
        </Box>
        <Alert severity="info">{t(view.reasonKey)}</Alert>
      </Box>
    );
  }

  const title = t(view.titleKey);
  return (
    <Box display="flex" flexDirection="column" gap={1}>
      <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
        <Typography variant="subtitle2">{title}</Typography>
        {view.kind === 'generated' && (
          <Typography variant="caption" color="text.secondary">
            {t(`${KEY}.generatedAt`, {
              date: view.generatedAt ? new Date(view.generatedAt).toLocaleString() : '',
            })}
          </Typography>
        )}
        {view.kind === 'generate' && (
          <LoadingButton size="small" variant="contained" loading={generating} onClick={generate}>
            {t(`${KEY}.generate.${view.documentKind}`)}
          </LoadingButton>
        )}
        {view.kind === 'generated' && (
          <>
            <LoadingButton
              size="small"
              variant="outlined"
              startIcon={<OpenInNew />}
              loading={downloading === 'view'}
              disabled={downloading !== null}
              onClick={() => void openFile('view')}
            >
              {t(`${KEY}.view`)}
            </LoadingButton>
            <LoadingButton
              size="small"
              variant="outlined"
              startIcon={<Download />}
              loading={downloading === 'download'}
              disabled={downloading !== null}
              onClick={() => void openFile('download')}
            >
              {t(`${KEY}.download`)}
            </LoadingButton>
            {allowSend && booking.source !== 'hotelrs' && voucherSendAvailable(view) && (
              <>
                <LoadingButton
                  size="small"
                  variant="outlined"
                  startIcon={<Send />}
                  loading={send.isLoading}
                  onClick={(event) => setSendAnchor(event.currentTarget)}
                >
                  {t(`${KEY}.send.button`)}
                </LoadingButton>
                <Menu anchorEl={sendAnchor} open={Boolean(sendAnchor)} onClose={() => setSendAnchor(null)}>
                  <MenuItem onClick={() => sendBy('email')}>{t(`${KEY}.send.email`)}</MenuItem>
                  <MenuItem onClick={() => sendBy('link')}>{t(`${KEY}.send.link`)}</MenuItem>
                </Menu>
              </>
            )}
          </>
        )}
      </Box>
      {view.kind === 'generated' && view.providerNoteKey && (
        <Typography variant="caption" color="text.secondary">
          {t(view.providerNoteKey)}
        </Typography>
      )}
      {view.kind === 'generated' &&
        voucherSendNotes(view.lastSends).map((note) => (
          <Typography key={note.key} variant="caption" color="text.secondary">
            {t(note.key, note.params)}
          </Typography>
        ))}
      {shareLink && (
        <TextField
          size="small"
          fullWidth
          value={shareLink.url}
          label={t(`${KEY}.send.linkField`)}
          helperText={t(`${KEY}.send.linkHint`)}
          InputProps={{ readOnly: true }}
          onFocus={(event) => event.target.select()}
        />
      )}
      {view.kind === 'generated' && view.attachedReferences.length > 0 && (
        <Typography variant="caption" color="text.secondary">
          {t(`${KEY}.attachedReferences`, { references: view.attachedReferences.join(', ') })}
        </Typography>
      )}
      {/* El documento sigue existiendo; lo que cambió es la reserva. Por eso
          es un aviso con acción y no un error. */}
      {view.kind === 'generated' && view.staleSince && (
        <Alert
          severity="warning"
          action={
            <LoadingButton
              size="small"
              color="inherit"
              startIcon={<Refresh />}
              loading={generating}
              onClick={generate}
            >
              {t(`${KEY}.regenerate`)}
            </LoadingButton>
          }
        >
          {t(`${KEY}.stale`, { date: new Date(view.staleSince).toLocaleString() })}
        </Alert>
      )}
      {generating && (
        <Typography variant="caption" color="text.secondary">
          {t(`${KEY}.generating`)}
        </Typography>
      )}
    </Box>
  );
}
